import { v4 as uuidv4 } from "uuid";
import { CraftingMaterialType, isCraftingMaterialType } from "@dndModels/Types";

/**
 * Something looted from a creature (or something else) that's neither money nor a regular item,
 * usually a body part or magical essence. These go in inventories alongside items, but separately,
 * and will eventually be used for crafting.
 *
 * weight and value are for one unit of the amount.
 */
export class CraftingMaterial {
  true_id = "";
  material_type: CraftingMaterialType = "Flesh";
  amount = 1;
  weight = 0; // In lb, per unit
  value = 0; // In gp, per unit
  // Where it came from. For a creature these are filled in from it,
  // otherwise source_other says what it came from.
  source_creature_id = "";
  source_name = ""; // The creature's name, for showing it
  source_creature_type = "";
  source_cr = -1;
  source_other = "";

  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.material_type = obj.material_type && isCraftingMaterialType(obj.material_type) ? obj.material_type : "Flesh";
      this.amount = obj.amount !== undefined ? +obj.amount : 1;
      this.weight = obj.weight !== undefined ? +obj.weight : 0;
      this.value = obj.value !== undefined ? +obj.value : 0;
      this.source_creature_id = obj.source_creature_id ? obj.source_creature_id : "";
      this.source_name = obj.source_name ? obj.source_name : "";
      this.source_creature_type = obj.source_creature_type ? obj.source_creature_type : "";
      this.source_cr = obj.source_cr !== undefined ? +obj.source_cr : -1;
      this.source_other = obj.source_other ? obj.source_other : "";
    }
  }

  get total_weight() {
    return this.amount * this.weight;
  }

  get total_value() {
    return this.amount * this.value;
  }

  get source_string() {
    if (this.source_creature_id !== "" || this.source_name !== "") {
      const details = [this.source_creature_type, this.source_cr >= 0 ? `CR ${this.source_cr}` : ""].filter(o => o !== "");
      return `${this.source_name}${details.length > 0 ? ` (${details.join(", ")})` : ""}`;
    }
    return this.source_other;
  }

  get name() {
    const source = this.source_name !== "" ? this.source_name : this.source_other;
    return source === "" ? this.material_type : `${source} ${this.material_type}`;
  }

  // Materials that are the same in every way but amount can be one stack.
  stacksWith(other: CraftingMaterial) {
    return this.material_type === other.material_type &&
      this.weight === other.weight &&
      this.value === other.value &&
      this.source_creature_id === other.source_creature_id &&
      this.source_name === other.source_name &&
      this.source_creature_type === other.source_creature_type &&
      this.source_cr === other.source_cr &&
      this.source_other === other.source_other;
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      material_type: this.material_type,
      amount: this.amount,
      weight: this.weight,
      value: this.value,
      source_creature_id: this.source_creature_id,
      source_name: this.source_name,
      source_creature_type: this.source_creature_type,
      source_cr: this.source_cr,
      source_other: this.source_other,
    };
  }

  clone(): CraftingMaterial {
    const new_obj = new CraftingMaterial();
    new_obj.copy(this);
    return new_obj;
  }

  copy(copyMe: CraftingMaterial): void {
    this.true_id = copyMe.true_id;
    this.material_type = copyMe.material_type;
    this.amount = copyMe.amount;
    this.weight = copyMe.weight;
    this.value = copyMe.value;
    this.source_creature_id = copyMe.source_creature_id;
    this.source_name = copyMe.source_name;
    this.source_creature_type = copyMe.source_creature_type;
    this.source_cr = copyMe.source_cr;
    this.source_other = copyMe.source_other;
  }
}

// Adds a material to a list, stacking it onto a matching one if there is one.
export const addCraftingMaterial = (materials: CraftingMaterial[], material: CraftingMaterial) => {
  const existing = materials.find(o => o.stacksWith(material));
  if (existing) {
    existing.amount += material.amount;
  } else {
    materials.push(material);
  }
};
